import type { ReactNode } from 'react';
import { BoxPicture } from '@src/components/BoxPicture/BoxPicture';
import { Spinner } from '@src/components/Spinner/Spinner';
import { testIdAttr, testIdFor } from '@src/components/testId';
import './PicturePicker.css';

export const BOX_PICKER_NAMES = [
  'santa',
  'cake',
  'cookie_star',
  'cookie_tree',
  'cup_cake',
  'cup_one',
  'house_one',
  'cup_two',
  'gift',
  'tree',
  'tree_toys',
  'lollipop',
  'mittens',
  'custom_image',
] as const;

export interface PicturePickerProps {
  value: string;
  onValueChange: (picture: string) => void;
  pictures?: readonly string[];
  /** Override the drawing in a tile — card faces use ParticipantFace. */
  renderPicture?: (picture: string) => ReactNode;
  logo?: string | null;
  /** Show a spinner on this tile while a custom upload is in flight. */
  loadingPicture?: string | null;
  disabled?: boolean;
  testId?: string;
}

export function PicturePicker({
  value,
  onValueChange,
  pictures = BOX_PICKER_NAMES,
  renderPicture,
  logo,
  loadingPicture,
  disabled = false,
  testId,
}: PicturePickerProps) {
  return (
    <div className="picture-picker" role="listbox" {...testIdAttr(testId)}>
      {pictures.map((picture) => {
        const selected = value === picture;
        const loading = loadingPicture === picture;
        const classes = ['picture-picker__tile'];
        if (selected) {
          classes.push('picture-picker__tile--selected');
        }
        if (loading) {
          classes.push('picture-picker__tile--loading');
        }
        return (
          <button
            key={picture}
            type="button"
            role="option"
            className={classes.join(' ')}
            aria-label={picture}
            aria-selected={selected}
            aria-busy={loading || undefined}
            disabled={disabled || loading}
            onClick={() => onValueChange(picture)}
            {...testIdAttr(testIdFor(testId, picture))}
          >
            <span className="picture-picker__art">
              {renderPicture ? (
                renderPicture(picture)
              ) : (
                <BoxPicture
                  picture={picture}
                  logo={picture === 'custom_image' ? logo : null}
                />
              )}
            </span>
            {loading ? (
              <span className="picture-picker__spinner">
                <Spinner size="medium" />
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
